// Legacy strategy migration engine (pure; browser + node safe).
//
// Scans a strategy's blocks, classifies every legacy `data.code` block, and AUTO-migrates only the EXACT
// canonical matches — stamping the trusted-TS `__canonical` marker, preserving parameters / connections /
// labels / layout / block id, dropping the executable body, and recording migration provenance. Convertible
// custom blocks (HTF EMA / Sweep+Failure / Displacement Gate) are flagged for the guided TSL Studio path;
// unrecognized bodies stay quarantined. Never guesses; never executes legacy JavaScript.

import { matchLegacyBlock, hashBody, type MatchResult } from './canonicalLegacyRegistry';

export type LegacyBlock = { id?: string; type?: string; data?: Record<string, unknown>; connections?: unknown; position?: unknown };

export type Classification = 'not-legacy' | 'canonical-exact' | 'convertible-custom' | 'manual-review' | 'unknown';

export interface ClassifiedBlock {
  id: string;
  name: string;
  blockType: string;
  classification: Classification;
  canonicalId?: string;
  canonicalVersion?: number;
  confidence?: MatchResult['confidence'];
  conversionTarget?: string;     // for convertible-custom: the TSL family to pre-fill
  reason: string;
  paramsPreserved: string[];     // param names that will carry over unchanged
}

// Convertible-custom recognition (structural, not name-based). These DON'T auto-migrate — they open the
// guided TSL Studio path with a best-effort pre-fill. Long/Short variants share a signature.
const CUSTOM_CONVERTERS: Array<{ target: string; blockType: string; entryFn: string; requiredParams: string[]; label: string }> = [
  { target: 'htf_ema', blockType: 'indicator', entryFn: 'calculate', requiredParams: ['htfFactor', 'htfMinutes', 'period'], label: 'HTF EMA 200' },
  { target: 'sweep_failure', blockType: 'pattern', entryFn: 'detectPattern', requiredParams: ['lookback'], label: 'Sweep + Failure' },
  { target: 'displacement_gate', blockType: 'filter', entryFn: 'analyze', requiredParams: ['atrPeriod', 'bodyMult', 'minAtr'], label: 'Displacement Gate' },
];

const paramNames = (code: string) => [...new Set([...String(code).matchAll(/params\.([A-Za-z_]\w*)/g)].map((m) => m[1]))].sort();
const isLegacyCode = (b: LegacyBlock) => b?.type === 'custom' && typeof b?.data?.code === 'string' && (b.data.code as string).trim().length > 0 && !(b.data as { tslAst?: unknown }).tslAst;

function matchCustom(block: LegacyBlock) {
  const code = String(block.data?.code ?? ''); const bt = block.data?.blockType;
  const fns = new Set([...code.matchAll(/function\s+([A-Za-z_]\w*)\s*\(/g)].map((m) => m[1]));
  const prms = new Set(paramNames(code));
  for (const c of CUSTOM_CONVERTERS) {
    if (bt === c.blockType && fns.has(c.entryFn) && c.requiredParams.length === prms.size && c.requiredParams.every((p) => prms.has(p))) return c;
  }
  return null;
}

/** Classify every block. Async because the exact canonical match uses a normalized-body hash. */
export async function classifyStrategy(blocks: LegacyBlock[]): Promise<{ items: ClassifiedBlock[]; summary: Record<Classification, number> & { total: number } }> {
  const items: ClassifiedBlock[] = [];
  for (const b of blocks ?? []) {
    const id = String(b?.id ?? '');
    const data = (b?.data ?? {}) as Record<string, unknown>;
    const name = String(data.name ?? data.label ?? 'custom block');
    const blockType = String(data.blockType ?? b?.type ?? '');
    if (!isLegacyCode(b)) { items.push({ id, name, blockType, classification: 'not-legacy', reason: 'not a legacy data.code block', paramsPreserved: [] }); continue; }

    const hash = (await hashBody(String(data.code))).slice(0, 16);
    const m = matchLegacyBlock(b as never, hash);
    if (m) {
      items.push({ id, name, blockType, classification: 'canonical-exact', canonicalId: m.descriptor.canonicalId, canonicalVersion: m.descriptor.canonicalVersion, confidence: m.confidence, reason: m.reason, paramsPreserved: m.descriptor.requiredParams });
      continue;
    }
    const cc = matchCustom(b);
    if (cc) { items.push({ id, name, blockType, classification: 'convertible-custom', conversionTarget: cc.target, reason: `recognized custom ${cc.label} — convert to TSL in the Studio`, paramsPreserved: paramNames(String(data.code)) }); continue; }

    items.push({ id, name, blockType, classification: 'manual-review', reason: 'legacy code does not match any known canonical or convertible signature — stays quarantined', paramsPreserved: paramNames(String(data.code)) });
  }
  const summary = { total: items.length, 'not-legacy': 0, 'canonical-exact': 0, 'convertible-custom': 0, 'manual-review': 0, unknown: 0 } as Record<Classification, number> & { total: number };
  for (const it of items) summary[it.classification]++;
  return { items, summary };
}

export interface MigrationReport {
  recovered: number;            // canonical blocks auto-migrated
  stillQuarantined: number;     // convertible-custom + manual-review (legacy, not yet migrated)
  unaffected: number;           // non-legacy blocks
  migratedIds: string[];
  items: ClassifiedBlock[];
}

/**
 * Apply the canonical auto-migration: rewrite ONLY 'canonical-exact' blocks. Each migrated block keeps its
 * id / type / position / connections / parameters / name, gains `parameters.__canonical = <id>`, loses
 * `data.code`, and records provenance in `data.__migration`. All other blocks are returned untouched.
 * Pure — the caller decides whether to save as a new revision / confirm overwrite, and can roll back by
 * keeping the original `blocks`.
 */
export async function applyCanonicalMigration(
  blocks: LegacyBlock[],
  opts: { nowIso: string; migrationVersion?: string },
): Promise<{ blocks: LegacyBlock[]; report: MigrationReport }> {
  const { items } = await classifyStrategy(blocks);
  const byId = new Map(items.map((it) => [it.id, it]));
  const migratedIds: string[] = [];

  const out = (blocks ?? []).map((b) => {
    const it = byId.get(String(b?.id ?? ''));
    if (!it || it.classification !== 'canonical-exact') return b;
    const data = { ...(b.data as Record<string, unknown>) };
    const params = { ...((data.parameters as Record<string, unknown>) ?? {}) };
    params.__canonical = it.canonicalId;                       // trusted-TS dispatch marker
    delete (data as { code?: unknown }).code;                  // remove the (quarantined) executable body
    data.parameters = params;
    data.__migration = {                                       // provenance
      canonicalId: it.canonicalId, canonicalVersion: it.canonicalVersion,
      source: 'legacy-canonical-auto', confidence: it.confidence,
      migratedAt: opts.nowIso, migrationVersion: opts.migrationVersion ?? '1',
    };
    migratedIds.push(it.id);
    return { ...b, data };                                     // id / type / position / connections preserved
  });

  const report: MigrationReport = {
    recovered: migratedIds.length,
    stillQuarantined: items.filter((it) => it.classification === 'convertible-custom' || it.classification === 'manual-review' || it.classification === 'unknown').length,
    unaffected: items.filter((it) => it.classification === 'not-legacy').length,
    migratedIds, items,
  };
  return { blocks: out, report };
}
